import { getApiClient } from '@signalvote/api-client';
import type { CreateTeamRequest, AddTeamMembersRequest, TeamDto, TeamDetailDto } from '@signalvote/shared';
import { defineQuery, defineWrite, requireSelectedCampaign } from '../../lib/data';

const api = getApiClient();

/** The campaign's teams. A write naming a team, a membership or a volunteer refreshes it. */
export const useTeams = defineQuery({
  key: () => ['teams', 'list'],
  reads: 'GET /api/Teams',
  fetch: (_: undefined, { campaignId }): Promise<TeamDto[]> => {
    requireSelectedCampaign(campaignId);
    return api.teams.getTeams();
  },
});

export const useTeam = defineQuery({
  key: (teamId: string) => ['teams', 'detail', teamId],
  reads: 'GET /api/Teams/{id}',
  fetch: (teamId, { campaignId }): Promise<TeamDetailDto> => {
    requireSelectedCampaign(campaignId);
    return api.teams.getTeam(teamId);
  },
});

/*
 * Team writes touch no cache: the server answers Team or TeamMembership, which the team reads and
 * the staff list (`useVolunteers`, each person's teams) declare.
 */

export const useCreateTeam = defineWrite({ send: (data: CreateTeamRequest) => api.teams.createTeam(data) });

export const useDeleteTeam = defineWrite({ send: (teamId: string) => api.teams.deleteTeam(teamId) });

export const useAddTeamMembers = defineWrite({
  send: ({ teamId, ...data }: AddTeamMembersRequest & { teamId: string }) => api.teams.addMembers(teamId, data),
});

export const useRemoveTeamMember = defineWrite({
  send: ({ teamId, userId }: { teamId: string; userId: string }) => api.teams.removeMember(teamId, userId),
});

/** Adds the same people to each team, one team at a time. */
export const useBulkAssignToTeams = defineWrite({
  send: async ({ teamIds, userIds }: { teamIds: string[]; userIds: string[] }) => {
    for (const teamId of teamIds) {
      await api.teams.addMembers(teamId, { userIds });
    }
  },
});
